import { useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Box,
  Calendar,
  CheckCircle2,
  Clock,
  CreditCard,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  MapPin,
  Phone,
  Receipt,
  Tag,
  User,
  XCircle,
} from 'lucide-react';
import {
  BosDurum,
  Buton,
  Iskelet,
  Kart,
  KartBaslik,
  KartIcerik,
  Rozet,
  Tablo,
  TabloBaslik,
  TabloBaslikHucresi,
  TabloGovde,
  TabloHucre,
  TabloSatir,
  cn,
} from '@emanetx/ui';
import type { KiralamaDetay, KiralamaOdemesi, KiralamaOlayi } from '@emanetx/sdk';
import { useKiralama } from './api.js';
import { DURUM_ETIKET, DURUM_TONU, kalanSureyiBicimle, telefonMaskele } from './durumlar.js';
import { KiralamaAksiyonDialog, type AksiyonTipi } from './kiralama-aksiyon-dialog.js';
import { KIRALAMA_AKTIF_DURUMLAR } from '@emanetx/sdk';

const ODEME_DURUMU_TONU = {
  BEKLEMEDE: 'turuncu',
  BASARILI: 'yesil',
  BASARISIZ: 'kirmizi',
  IADE_EDILDI: 'mavi',
} as const;

const ODEME_DURUMU_ETIKET = {
  BEKLEMEDE: 'Beklemede',
  BASARILI: 'Başarılı',
  BASARISIZ: 'Başarısız',
  IADE_EDILDI: 'İade',
} as const;

const OLAY_ETIKET: Record<string, string> = {
  olusturuldu: 'Kiralama oluşturuldu',
  odeme_basarili: 'Ödeme başarılı',
  odeme_basarisiz: 'Ödeme başarısız',
  dolap_acildi: 'Dolap açıldı',
  kapi_kapandi: 'Kapı kapandı',
  teslim_alindi: 'Eşya teslim alındı',
  tamamlandi: 'Kiralama tamamlandı',
  iptal_edildi: 'İptal edildi',
  zorla_sonlandirildi: 'Zorla sonlandırıldı',
};

export function KiralamaDetayIcerik({ kiralamaId }: { kiralamaId: string }) {
  const sorgu = useKiralama(kiralamaId);

  if (sorgu.isLoading) {
    return (
      <div className="space-y-4">
        <Iskelet className="h-10 w-1/3" />
        <Iskelet className="h-40" />
        <Iskelet className="h-40" />
      </div>
    );
  }

  if (sorgu.isError || !sorgu.data) {
    return (
      <div className="border-fume-200 dark:border-fume-800 dark:bg-fume-900 rounded-xl border bg-white p-6 text-center">
        <p className="text-fume-600 dark:text-fume-300 text-sm">Kiralama bulunamadı.</p>
      </div>
    );
  }

  return <Icerik kiralama={sorgu.data} />;
}

function Icerik({ kiralama: k }: { kiralama: KiralamaDetay }) {
  const kalan = kalanSureyiBicimle(k.planlananBitis, k.bitis ?? null);
  const sembol = k.paraBirimi === 'TRY' ? '₺' : k.paraBirimi === 'EUR' ? '€' : '$';
  const aktif = !k.bitis;
  const aktifDurum = KIRALAMA_AKTIF_DURUMLAR.includes(k.durum);
  const basariliOdemeVar = k.odemeler.some((o) => o.durum === 'BASARILI');

  const [aksiyonTipi, setAksiyonTipi] = useState<AksiyonTipi | null>(null);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-2xl font-bold tracking-tight">
            <span className="text-marka-700 dark:text-marka-400 font-mono">{k.kod}</span>
          </h2>
          <Rozet ton={DURUM_TONU[k.durum]} nokta>
            {DURUM_ETIKET[k.durum]}
          </Rozet>
          {aktif && (
            <Rozet ton={kalan.asti ? 'kirmizi' : 'fume'}>
              <Clock size={11} /> {kalan.metin}
            </Rozet>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {aktifDurum && (
            <>
              <Buton stil="cizgili" boyut="kucuk" onClick={() => setAksiyonTipi('manuel')}>
                <KeyRound size={13} /> Manuel Aç
              </Buton>
              <Buton stil="tehlike" boyut="kucuk" onClick={() => setAksiyonTipi('zorla')}>
                <AlertTriangle size={13} /> Zorla Sonlandır
              </Buton>
            </>
          )}
          {basariliOdemeVar && (
            <Buton stil="cizgili" boyut="kucuk" onClick={() => setAksiyonTipi('iade')}>
              <Receipt size={13} /> İade Başlat
            </Buton>
          )}
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-3">
        <Kart className="lg:col-span-2">
          <KartBaslik>
            <h3 className="text-base font-semibold">Özet</h3>
          </KartBaslik>
          <KartIcerik className="grid gap-3 sm:grid-cols-2">
            <Alan
              ikon={MapPin}
              etiket="Nokta"
              deger={
                <span>
                  <span className="text-fume-500 font-mono">{k.noktaKod ?? '—'}</span>
                  {k.noktaAd && <span className="text-fume-600 ml-2">{k.noktaAd}</span>}
                </span>
              }
            />
            <Alan
              ikon={Box}
              etiket="Dolap"
              deger={<span className="font-mono">#{k.dolapNumara ?? '—'}</span>}
            />
            <Alan
              ikon={Phone}
              etiket="Telefon"
              deger={<span className="font-mono">{telefonMaskele(k.telefon)}</span>}
            />
            <Alan
              ikon={User}
              etiket="Müşteri"
              deger={
                k.adSoyad ?? (
                  <span className="text-fume-400">
                    {k.musteriId ? 'Kayıtlı müşteri' : 'Misafir'}
                  </span>
                )
              }
            />
            <PinAlani pin={k.pinKodu} />
            <Alan
              ikon={Calendar}
              etiket="Başlangıç"
              deger={new Date(k.baslangic).toLocaleString('tr-TR')}
            />
            <Alan
              ikon={Calendar}
              etiket="Planlanan bitiş"
              deger={new Date(k.planlananBitis).toLocaleString('tr-TR')}
            />
            <Alan
              ikon={CheckCircle2}
              etiket="Gerçek bitiş"
              deger={
                k.bitis ? (
                  new Date(k.bitis).toLocaleString('tr-TR')
                ) : (
                  <span className="text-fume-400">Devam ediyor</span>
                )
              }
            />
            {k.kuponKodu && (
              <Alan
                ikon={Tag}
                etiket="Kupon"
                deger={<span className="font-mono">{k.kuponKodu}</span>}
              />
            )}
          </KartIcerik>
        </Kart>

        <Kart>
          <KartBaslik>
            <h3 className="text-base font-semibold">Tutarlar</h3>
          </KartBaslik>
          <KartIcerik className="space-y-2.5">
            <SatirTutar etiket="Toplam" deger={`${sembol}${k.toplamTutar}`} buyuk />
            <SatirTutar etiket="Ödenen" deger={`${sembol}${k.odenenTutar}`} />
            {Number(k.uygulananIndirim) > 0 && (
              <SatirTutar
                etiket="İndirim"
                deger={`−${sembol}${k.uygulananIndirim}`}
                renk="text-emerald-600 dark:text-emerald-400"
              />
            )}
            {Number(k.cezaTutari) > 0 && (
              <SatirTutar
                etiket="Ceza"
                deger={`${sembol}${k.cezaTutari}`}
                renk="text-red-600 dark:text-red-400"
              />
            )}
            {Number(k.depozito) > 0 && (
              <SatirTutar etiket="Depozito" deger={`${sembol}${k.depozito}`} />
            )}
            <div className="border-fume-100 dark:border-fume-800 border-t pt-2">
              <p className="text-fume-400 text-xs">Para birimi: {k.paraBirimi}</p>
            </div>
          </KartIcerik>
        </Kart>
      </div>

      <Kart>
        <KartBaslik>
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <Activity size={16} className="text-marka-500" /> Olay Zaman Çizelgesi
          </h3>
        </KartBaslik>
        <KartIcerik>
          {k.olaylar.length === 0 ? (
            <BosDurum ikon={Activity} baslik="Henüz olay yok" />
          ) : (
            <ol className="space-y-3">
              {k.olaylar.map((o, i) => (
                <OlayOgesi key={o.id} olay={o} sonuncu={i === k.olaylar.length - 1} />
              ))}
            </ol>
          )}
        </KartIcerik>
      </Kart>

      <Kart>
        <KartBaslik>
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <Receipt size={16} className="text-marka-500" /> Ödemeler
          </h3>
        </KartBaslik>
        <KartIcerik>
          {k.odemeler.length === 0 ? (
            <BosDurum ikon={CreditCard} baslik="Henüz ödeme kaydı yok" />
          ) : (
            <Tablo>
              <TabloBaslik>
                <tr>
                  <TabloBaslikHucresi>Zaman</TabloBaslikHucresi>
                  <TabloBaslikHucresi>Tip</TabloBaslikHucresi>
                  <TabloBaslikHucresi>Tutar</TabloBaslikHucresi>
                  <TabloBaslikHucresi>Durum</TabloBaslikHucresi>
                  <TabloBaslikHucresi className="hidden md:table-cell">Referans</TabloBaslikHucresi>
                </tr>
              </TabloBaslik>
              <TabloGovde>
                {k.odemeler.map((o) => (
                  <OdemeSatiri key={o.id} odeme={o} sembol={sembol} />
                ))}
              </TabloGovde>
            </Tablo>
          )}
        </KartIcerik>
      </Kart>

      {k.notlar && (
        <Kart>
          <KartBaslik>
            <h3 className="text-base font-semibold">Notlar</h3>
          </KartBaslik>
          <KartIcerik>
            <p className="text-fume-700 dark:text-fume-200 whitespace-pre-wrap text-sm">
              {k.notlar}
            </p>
          </KartIcerik>
        </Kart>
      )}

      {aksiyonTipi && (
        <KiralamaAksiyonDialog
          acik={!!aksiyonTipi}
          onDegisim={(o) => !o && setAksiyonTipi(null)}
          kiralama={k}
          tip={aksiyonTipi}
        />
      )}
    </div>
  );
}

function PinAlani({ pin }: { pin: string }) {
  const [goster, setGoster] = useState(false);
  return (
    <div className="flex items-start gap-3">
      <Lock size={16} className="text-fume-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-fume-500 text-xs">PIN</p>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-fume-800 dark:text-fume-100 font-mono text-lg tracking-wider">
            {goster ? pin : '••••••'}
          </span>
          <button
            type="button"
            onClick={() => setGoster((g) => !g)}
            className="text-fume-400 hover:text-fume-700 dark:hover:text-fume-100"
            aria-label={goster ? 'Gizle' : 'Göster'}
          >
            {goster ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
}

function Alan({
  ikon: Ikon,
  etiket,
  deger,
}: {
  ikon: React.ComponentType<{ size?: number; className?: string }>;
  etiket: string;
  deger: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <Ikon size={16} className="text-fume-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-fume-500 text-xs">{etiket}</p>
        <div className="text-fume-800 dark:text-fume-100 break-words text-sm">{deger}</div>
      </div>
    </div>
  );
}

function SatirTutar({
  etiket,
  deger,
  buyuk,
  renk,
}: {
  etiket: string;
  deger: string;
  buyuk?: boolean;
  renk?: string;
}) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-fume-500 dark:text-fume-400 text-sm">{etiket}</span>
      <span
        className={cn(
          'font-mono tabular-nums',
          buyuk ? 'text-xl font-bold' : 'text-sm',
          renk ?? 'text-fume-900 dark:text-fume-50',
        )}
      >
        {deger}
      </span>
    </div>
  );
}

function OlayOgesi({ olay: o, sonuncu }: { olay: KiralamaOlayi; sonuncu: boolean }) {
  const etiket = OLAY_ETIKET[o.tip] ?? o.tip;
  return (
    <li className="relative flex gap-3 pl-1">
      <div className="flex flex-col items-center">
        <div className="bg-marka-100 text-marka-700 dark:bg-marka-900/40 dark:text-marka-400 grid h-6 w-6 place-items-center rounded-full">
          <CheckCircle2 size={12} />
        </div>
        {!sonuncu && <div className="bg-fume-200 dark:bg-fume-700 mt-1 w-px flex-1" />}
      </div>
      <div className="flex-1 pb-3">
        <p className="text-fume-900 dark:text-fume-50 text-sm font-medium">{etiket}</p>
        {o.aciklama && <p className="text-fume-500 mt-0.5 text-xs">{o.aciklama}</p>}
        <p className="text-fume-400 mt-0.5 text-xs">
          {new Date(o.olusturmaTarihi).toLocaleString('tr-TR', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })}
          {o.yapanTipi && <span className="text-fume-400 ml-2">— {o.yapanTipi}</span>}
        </p>
      </div>
    </li>
  );
}

function OdemeSatiri({ odeme: o, sembol }: { odeme: KiralamaOdemesi; sembol: string }) {
  const tona = ODEME_DURUMU_TONU[o.durum];
  return (
    <TabloSatir>
      <TabloHucre className="text-fume-500 font-mono text-xs">
        {new Date(o.olusturmaTarihi).toLocaleString('tr-TR', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </TabloHucre>
      <TabloHucre>
        <Rozet ton="fume">{o.saglayici}</Rozet>
      </TabloHucre>
      <TabloHucre className="font-mono">
        {sembol}
        {o.miktar}
      </TabloHucre>
      <TabloHucre>
        <Rozet ton={tona} nokta>
          {ODEME_DURUMU_ETIKET[o.durum]}
        </Rozet>
        {o.hataMesaji && (
          <span className="ml-2 inline-flex items-center gap-1 text-xs text-red-600">
            <XCircle size={11} /> {o.hataMesaji}
          </span>
        )}
      </TabloHucre>
      <TabloHucre className="text-fume-400 hidden font-mono text-xs md:table-cell">
        {o.saglayiciRef ?? '—'}
      </TabloHucre>
    </TabloSatir>
  );
}
